import React, { useState, useRef } from "react";
import styles from "./file-upload.module.css"; // Import CSS Module
import { Button } from "../ui/Button/Button";
import { DeleteButton } from "../ui/DeleteButton/DeleteButton";
import Loader from "../ui/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { uploadFiles } from "../../services/actions/currentProjects";

const MIN_FILE_SIZE_MB = 10; // Минимальный размер файла в мегабайтах
const MAX_FILE_SIZE_MB = 1024; // Максимальный размер файла в мегабайтах
const ACCEPTED_FILE_TYPES = [".fastq", ".gz"];

const FileUpload = (props) => {
  const [files, setFiles] = useState([]);
  const [isOverMaxFiles, setIsOverMaxFiles] = useState(false);
  const [fileSizeError, setFileSizeError] = useState("");
  const [fileTypeError, setFileTypeError] = useState("");
  const dispatch = useDispatch();
  const { token } = useSelector((store) => store.user);
  const { createProjectId, filesUploadRequest, filesUploadSuccess } =
    useSelector((store) => store.projectList);
  const fileInputRef = useRef(null);

  const isAcceptedFileType = (file) => {
    return ACCEPTED_FILE_TYPES.some((type) => file.name.endsWith(type));
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    handleFiles(selectedFiles);
  };

  const checkMaxFiles = (filesForCheck) => {
    setIsOverMaxFiles(filesForCheck.length > 2);
  };

  const handleFiles = (newFiles) => {
    const validFiles = [];
    let hasErrors = false;

    newFiles.forEach((file) => {
      const fileSizeMB = file.size / (1024 * 1024); // Размер файла в мегабайтах
      if (!isAcceptedFileType(file)) {
        setFileTypeError(
          `Файл "${
            file.name
          }" имеет неподдерживаемый формат. Допустимые форматы: ${ACCEPTED_FILE_TYPES.join(
            ", "
          )}`
        );
        hasErrors = true;
      } else if (
        fileSizeMB < MIN_FILE_SIZE_MB ||
        fileSizeMB > MAX_FILE_SIZE_MB
      ) {
        setFileSizeError(
          `Размер файла "${file.name}" не соответствует допустимым ${MIN_FILE_SIZE_MB} МБ - ${MAX_FILE_SIZE_MB} МБ`
        );
        hasErrors = true;
      } else {
        validFiles.push(file);
      }
    });

    if (!hasErrors) {
      setFileSizeError("");
      setFileTypeError("");
    }

    checkMaxFiles([...files, ...validFiles]);
    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (fileToRemove) => {
    const updatedFiles = files.filter(
      (file) => file.name !== fileToRemove.name
    );
    checkMaxFiles(updatedFiles);
    setFiles(updatedFiles);
  };

  const handleUpload = () => {
    dispatch(uploadFiles(token, createProjectId, files));
  };

  const getFileSizeInMB = (file) => {
    const sizeInBytes = file.size;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return Math.round(sizeInMB * 10) / 10;
  };

  return (
    <div className={styles.fileUpload}>
      {filesUploadRequest && <Loader />}
      {filesUploadSuccess && (
        <p className={styles.successMessage}>
          Файлы успешно загружены, процесс анализа работает в фоне, вы можете
          пользоваться системой
        </p>
      )}
      {!filesUploadRequest && !filesUploadSuccess && (
        <>
        <div className={styles.uploadWrapper}>
          {files.length > 0 && (
            <div
              className={`${styles.fileList} ${
                isOverMaxFiles ? styles.dropArea_error : ""
              }`}
            >
              {files.map((file, index) => (
                <div key={index} className={styles.fileItem}>
                  <span className={styles.fileName}>
                    {index + 1}. {file.name}
                  </span>
                  <span className={styles.fileSize}>
                    {getFileSizeInMB(file)}Мб
                  </span>
                  <DeleteButton onClick={() => removeFile(file)} />
                </div>
              ))}
            </div>
          )}
          {(fileSizeError || fileTypeError) && (
            <p className={styles.errorMessage}>
              {fileSizeError || fileTypeError}
            </p>
          )}
          <div
            className={`${styles.dropArea} ${
              isOverMaxFiles ? styles.dropArea_error : ""
            } ${files.length === 2 ? styles.dropArea_blurred : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => !isOverMaxFiles && fileInputRef.current.click()}
          >
            {isOverMaxFiles ? (
              <p className={styles.errorMessage}>
                Превышено максимальное количество файлов
              </p>
            ) : (
              <>
                <span className={styles.uploadIcon}>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14 0.5625C14.1354 0.562414 14.2691 0.591643 14.3921 0.648177C14.5151 0.704711 14.6244 0.787209 14.7125 0.89L18.4625 5.265C18.6243 5.45397 18.7044 5.69946 18.6852 5.94748C18.6659 6.1955 18.549 6.42572 18.36 6.5875C18.171 6.74928 17.9255 6.82937 17.6775 6.81015C17.4295 6.79093 17.1993 6.67397 17.0375 6.485L14.9375 4.035V17.75C14.9375 17.9986 14.8387 18.2371 14.6629 18.4129C14.4871 18.5887 14.2486 18.6875 14 18.6875C13.7514 18.6875 13.5129 18.5887 13.3371 18.4129C13.1613 18.2371 13.0625 17.9986 13.0625 17.75V4.03375L10.9625 6.485C10.8824 6.57857 10.7846 6.65544 10.6748 6.71123C10.565 6.76702 10.4453 6.80063 10.3225 6.81015C10.1997 6.81967 10.0762 6.8049 9.9591 6.7667C9.842 6.7285 9.73357 6.66761 9.64 6.5875C9.54643 6.50739 9.46956 6.40964 9.41377 6.29983C9.35798 6.19001 9.32437 6.07028 9.31485 5.94748C9.30533 5.82467 9.3201 5.7012 9.3583 5.5841C9.3965 5.467 9.45739 5.35857 9.5375 5.265L13.2875 0.89C13.3756 0.787209 13.4849 0.704711 13.6079 0.648177C13.7309 0.591643 13.8646 0.562414 14 0.5625ZM7.745 9.315C7.99364 9.31367 8.23262 9.41117 8.40938 9.58605C8.58613 9.76093 8.68617 9.99886 8.6875 10.2475C8.68883 10.4961 8.59133 10.7351 8.41645 10.9119C8.24157 11.0886 8.00364 11.1887 7.755 11.19C6.38875 11.1975 5.42 11.2325 4.68375 11.3675C3.97625 11.4987 3.565 11.7075 3.26125 12.0113C2.915 12.3575 2.69 12.8438 2.56625 13.7612C2.44 14.705 2.4375 15.9563 2.4375 17.75V19C2.4375 20.795 2.44 22.0462 2.56625 22.99C2.69 23.9075 2.91625 24.3925 3.26125 24.74C3.6075 25.085 4.0925 25.31 5.01125 25.4338C5.95375 25.5612 7.20625 25.5625 9 25.5625H19C20.7938 25.5625 22.045 25.5612 22.99 25.4338C23.9075 25.31 24.3925 25.085 24.7388 24.7388C25.085 24.3925 25.31 23.9075 25.4338 22.99C25.56 22.0462 25.5625 20.795 25.5625 19V17.75C25.5625 15.9563 25.56 14.705 25.4338 13.76C25.31 12.8438 25.0837 12.3575 24.7388 12.0113C24.4338 11.7075 24.0237 11.4987 23.3162 11.3675C22.58 11.2325 21.6112 11.1975 20.245 11.19C20.1219 11.1893 20.0001 11.1644 19.8866 11.1167C19.7731 11.069 19.6701 10.9994 19.5836 10.9119C19.497 10.8244 19.4285 10.7206 19.3819 10.6066C19.3354 10.4927 19.3118 10.3706 19.3125 10.2475C19.3132 10.1244 19.3381 10.0026 19.3858 9.88912C19.4335 9.77562 19.5031 9.67264 19.5906 9.58605C19.6781 9.49946 19.7819 9.43096 19.8959 9.38445C20.0098 9.33794 20.1319 9.31434 20.255 9.315C21.6075 9.3225 22.7338 9.355 23.655 9.52375C24.6025 9.69875 25.4088 10.03 26.065 10.6862C26.8175 11.4375 27.14 12.3863 27.2925 13.5113C27.4375 14.5938 27.4375 15.9725 27.4375 17.6812V19.0688C27.4375 20.7788 27.4375 22.1562 27.2925 23.24C27.14 24.365 26.8175 25.3125 26.065 26.065C25.3125 26.8175 24.365 27.14 23.24 27.2925C22.1562 27.4375 20.7775 27.4375 19.0688 27.4375H8.93125C7.2225 27.4375 5.84375 27.4375 4.76 27.2925C3.635 27.1413 2.6875 26.8175 1.935 26.065C1.1825 25.3125 0.86 24.365 0.70875 23.24C0.5625 22.1562 0.5625 20.7775 0.5625 19.0688V17.6812C0.5625 15.9725 0.5625 14.5938 0.70875 13.51C0.85875 12.385 1.18375 11.4375 1.935 10.685C2.59125 10.03 3.3975 9.6975 4.345 9.52375C5.26625 9.355 6.3925 9.3225 7.745 9.315Z"
                      fill="#1E1E1E"
                    />
                  </svg>
                </span>
                <p>Перетащите файлы сюда или нажмите, чтобы загрузить</p>
                <p className={styles.bottomText}>
                  Подгружать fastq файлы только от одного клинического образца.{" "}
                  <br />
                  Максимальный размер файлов: не более 1 Гб
                  <br />
                  Максимальное количество файлов: 2<br />
                  Минимальный размер файла: 10 МБ
                </p>
              </>
            )}
            <input
              type="file"
              multiple
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={files.length === 2}
              accept=".fastq, .gz"
            />
          </div>
        </div>
        <Button
            value="Запустить анализ проекта"
            onClick={handleUpload}
            disabled={isOverMaxFiles || files.length === 0}
          />
        </>
      )}
    </div>
  );
};

export default FileUpload;
